import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAIProviders } from '../hooks/useAIProviders';
import { CreateSmartColumnInput, UpdateSmartColumnInput, SmartAIColumn, AIProvider, AIColumnTemplate, smartColumnsApi, ActionsConfig } from '../api/ai.api';
import { 
  Bot, 
  Sparkles, 
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Trash2,
  Zap,
  FolderInput,
  Tag,
  ListTodo,
  Play
} from 'lucide-react';
import api from '../../../api/client';

interface FolderInfo {
  id: string;
  name: string;
  type: 'folder' | 'category' | 'label';
}

interface SmartColumnCreatorProps {
  boardId: string;
  folders: FolderInfo[];
  mailAccountId?: string; // For loading task lists
  onClose: () => void;
  onCreate: (input: CreateSmartColumnInput) => Promise<void>;
  // Edit mode
  editColumn?: SmartAIColumn;
  onUpdate?: (columnId: string, input: UpdateSmartColumnInput) => Promise<void>;
  onDelete?: (columnId: string) => Promise<void>;
}

// Types for external APIs
interface ExternalTodoList {
  id: string;
  name: string;
  isDefault?: boolean;
}

const PROMPT_EXAMPLES = [
  { label: '🔥 Dringend', prompt: 'E-Mails die heute noch beantwortet werden müssen oder eine Deadline haben' },
  { label: '💰 Sales Leads', prompt: 'Anfragen von potenziellen Kunden oder Interessenten an unseren Produkten/Services' },
  { label: '📋 Action Items', prompt: 'E-Mails mit konkreten Aufgaben oder Handlungsaufforderungen für mich' },
  { label: '🤝 Follow-Up', prompt: 'Konversationen bei denen ich auf eine Antwort warte' },
  { label: '📅 Meeting-Bezug', prompt: 'E-Mails die sich auf bevorstehende Kalender-Events oder Meetings beziehen' },
  { label: '🆘 Support', prompt: 'Kundenanfragen mit Problemen oder technischen Fragen die Hilfe benötigen' },
];

export function SmartColumnCreator({ boardId, folders, mailAccountId, onClose, onCreate, editColumn, onUpdate, onDelete }: SmartColumnCreatorProps) {
  const { providers } = useAIProviders();
  const isEditMode = !!editColumn;
  
  // Fetch templates from backend
  const { data: templates = [] } = useQuery({
    queryKey: ['ai-column-templates'],
    queryFn: () => smartColumnsApi.getTemplates(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  // Fetch task lists for the mail account
  const { data: taskLists = [] } = useQuery({
    queryKey: ['todo-lists', mailAccountId],
    queryFn: async () => {
      if (!mailAccountId) return [];
      const res = await api.get(`todo/${mailAccountId}/lists`).json<{ lists: ExternalTodoList[] }>();
      return res.lists;
    },
    enabled: !!mailAccountId,
    staleTime: 5 * 60 * 1000,
  });
  
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [baseSourceType, setBaseSourceType] = useState<'folder' | 'category' | 'label' | 'all'>('folder');
  const [baseSourceId, setBaseSourceId] = useState<string>('INBOX');
  const [providerId, setProviderId] = useState<string>(providers.find(p => p.isDefault)?.id || '');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [runningActions, setRunningActions] = useState(false);
  const [runActionsResult, setRunActionsResult] = useState<{ processed: number; executed: string[]; errors: string[] } | null>(null);
  
  // Confidence Threshold (0-100)
  const [confidenceThreshold, setConfidenceThreshold] = useState(80);
  
  // Advanced settings
  const [color, setColor] = useState('#8B5CF6'); // purple
  const [icon, setIcon] = useState('🤖');
  const [maxReEvals, setMaxReEvals] = useState(5);
  const [maxTokensPerDay, setMaxTokensPerDay] = useState(50000);
  
  // Actions settings
  const [actionsEnabled, setActionsEnabled] = useState(false);
  const [actionMoveEnabled, setActionMoveEnabled] = useState(false);
  const [actionMoveFolder, setActionMoveFolder] = useState<string>('');
  const [actionCategoryEnabled, setActionCategoryEnabled] = useState(false);
  const [actionCategory, setActionCategory] = useState<string>('');
  const [actionTaskEnabled, setActionTaskEnabled] = useState(false);
  const [actionTaskListId, setActionTaskListId] = useState<string>('');
  const [actionTaskTitleMode, setActionTaskTitleMode] = useState<'subject' | 'custom' | 'ai'>('subject');
  const [actionTaskCustomTitle, setActionTaskCustomTitle] = useState('');
  const [actionTaskDueDays, setActionTaskDueDays] = useState<number | undefined>(undefined);
  const [actionTaskIncludeLink, setActionTaskIncludeLink] = useState(true);
  const [actionExecuteOnce, setActionExecuteOnce] = useState(true);
  const [actionMinConfidence, setActionMinConfidence] = useState<number | undefined>(undefined);
  
  // Initialize with edit data when in edit mode
  useEffect(() => {
    if (editColumn) {
      setName(editColumn.name || '');
      setPrompt(editColumn.prompt || '');
      setBaseSourceType(editColumn.baseSourceType || 'all');
      setBaseSourceId(editColumn.baseSourceId || 'all');
      setProviderId(editColumn.providerId || providers.find(p => p.isDefault)?.id || '');
      setConfidenceThreshold(editColumn.confidenceThreshold ?? 80);
      setColor(editColumn.color || '#8B5CF6');
      setIcon(editColumn.icon || '🤖');
      setMaxReEvals(editColumn.reEvalConfig?.maxReEvals || 5);
      setMaxTokensPerDay(editColumn.costConfig?.maxTokensPerDay || 50000);
      setShowAdvanced(true); // Show advanced in edit mode
      
      // Load actions config
      if (editColumn.actionsConfig) {
        setActionsEnabled(true);
        setShowActions(true);
        const actions = editColumn.actionsConfig;
        
        if (actions.onMatch.moveToFolder) {
          setActionMoveEnabled(true);
          setActionMoveFolder(actions.onMatch.moveToFolder);
        }
        if (actions.onMatch.setCategory) {
          setActionCategoryEnabled(true);
          setActionCategory(actions.onMatch.setCategory);
        }
        if (actions.onMatch.createTask) {
          setActionTaskEnabled(true);
          setActionTaskListId(actions.onMatch.createTask.listId);
          setActionTaskTitleMode(actions.onMatch.createTask.titleMode);
          setActionTaskCustomTitle(actions.onMatch.createTask.customTitle || '');
          setActionTaskDueDays(actions.onMatch.createTask.dueDays);
          setActionTaskIncludeLink(actions.onMatch.createTask.includeMailLink);
        }
        
        setActionExecuteOnce(actions.executeOnce);
        setActionMinConfidence(actions.requireMinConfidence ?? undefined);
      }
    }
  }, [editColumn, providers]);

  // Build actions config from state
  const buildActionsConfig = (): ActionsConfig | null => {
    if (!actionsEnabled) return null;
    
    const hasAnyAction = actionMoveEnabled || actionCategoryEnabled || actionTaskEnabled;
    if (!hasAnyAction) return null;
    
    return {
      onMatch: {
        moveToFolder: actionMoveEnabled && actionMoveFolder ? actionMoveFolder : null,
        setCategory: actionCategoryEnabled && actionCategory ? actionCategory : null,
        createTask: actionTaskEnabled && actionTaskListId ? {
          listId: actionTaskListId,
          titleMode: actionTaskTitleMode,
          customTitle: actionTaskTitleMode === 'custom' ? actionTaskCustomTitle : undefined,
          dueDays: actionTaskDueDays,
          includeMailLink: actionTaskIncludeLink,
        } : null,
      },
      executeOnce: actionExecuteOnce,
      requireMinConfidence: actionMinConfidence ?? null,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    
    setLoading(true);
    setError(null);
    
    const actionsConfig = buildActionsConfig();
    
    try {
      if (isEditMode && editColumn && onUpdate) {
        // Update existing column
        await onUpdate(editColumn.id, {
          name: name || prompt.slice(0, 30) + (prompt.length > 30 ? '...' : ''),
          prompt: prompt.trim(),
          baseSourceType,
          baseSourceId: baseSourceType === 'all' ? 'all' : baseSourceId,
          providerId: providerId || null,
          confidenceThreshold,
          color,
          icon,
          actionsConfig,
          reEvalConfig: {
            onThreadUpdate: true,
            onDeadlineApproach: true,
            maxReEvals,
          },
          costConfig: {
            maxTokensPerDay,
            maxEvalsPerHour: 100,
            skipConfidenceAbove: 0.95,
            cacheDays: 30,
          },
        });
      } else {
        // Create new column
        await onCreate({
          boardId,
          name: name || prompt.slice(0, 30) + (prompt.length > 30 ? '...' : ''),
          prompt: prompt.trim(),
          baseSourceType,
          baseSourceId: baseSourceType === 'all' ? 'all' : baseSourceId,
          providerId: providerId || undefined,
          confidenceThreshold,
          color,
          icon,
          actionsConfig,
          reEvalConfig: {
            onThreadUpdate: true,
            onDeadlineApproach: true,
            maxReEvals,
          },
          costConfig: {
            maxTokensPerDay,
            maxEvalsPerHour: 100,
            skipConfidenceAbove: 0.95,
            cacheDays: 30,
          },
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : isEditMode ? 'Fehler beim Speichern' : 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!editColumn || !onDelete) return;
    setLoading(true);
    try {
      await onDelete(editColumn.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };
  
  // Run actions once for all currently matched emails
  const handleRunActionsOnce = async () => {
    if (!editColumn || !mailAccountId) return;
    setRunningActions(true);
    setRunActionsResult(null);
    try {
      const result = await smartColumnsApi.runActionsOnce(editColumn.id, mailAccountId);
      setRunActionsResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Ausführen');
    } finally {
      setRunningActions(false);
    }
  };

  const applyExample = (example: typeof PROMPT_EXAMPLES[0]) => {
    setPrompt(example.prompt);
    if (!name) {
      setName(example.label.replace(/^[^\w]+/, '').trim());
    }
  };

  const applyTemplate = (template: AIColumnTemplate) => {
    setName(template.name);
    setPrompt(template.prompt);
    setIcon(template.icon);
    setColor(template.color);
    setConfidenceThreshold(template.confidenceThreshold);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold">
              {isEditMode ? 'AI-Spalte bearbeiten' : 'Smart AI Column'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Provider Warning */}
          {providers.length === 0 && (
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-lg text-sm flex items-start gap-2">
              <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Kein AI Provider konfiguriert!</strong>
                <p>Gehe zu Einstellungen → AI Provider um einen API Key hinzuzufügen.</p>
              </div>
            </div>
          )}
          
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Spaltenname</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="z.B. Dringende Mails"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
            />
          </div>
          
          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium mb-1">Kriterium / Prompt *</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Beschreibe welche E-Mails in diese Spalte sollen..."
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Die AI analysiert jede E-Mail und entscheidet ob sie zu diesem Kriterium passt.
            </p>
          </div>
          
          {/* Templates from Backend */}
          {!isEditMode && templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                Schnellstart-Templates
              </label>
              <div className="grid grid-cols-2 gap-2">
                {templates.map(template => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="p-2 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all group"
                    style={{ borderLeftColor: template.color, borderLeftWidth: '3px' }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{template.icon}</span>
                      <span className="text-sm font-medium truncate">{template.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                      {template.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Prompt Examples (fallback for quick ideas) */}
          <div>
            <label className="block text-sm font-medium mb-2">Schnell-Ideen</label>
            <div className="flex flex-wrap gap-2">
              {PROMPT_EXAMPLES.map(example => (
                <button
                  key={example.label}
                  type="button"
                  onClick={() => applyExample(example)}
                  className="px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Base Folder/Category */}
          <div>
            <label className="block text-sm font-medium mb-1">E-Mail Quelle</label>
            <select
              value={baseSourceType === 'all' ? 'all' : `${baseSourceType}:${baseSourceId}`}
              onChange={e => {
                const val = e.target.value;
                if (val === 'all') {
                  setBaseSourceType('all');
                  setBaseSourceId('all');
                } else {
                  const [type, id] = val.split(':');
                  setBaseSourceType(type as 'folder' | 'category' | 'label');
                  setBaseSourceId(id);
                }
              }}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
            >
              <option value="all">Alle E-Mails</option>
              {folders.map(f => (
                <option key={`${f.type}:${f.id}`} value={`${f.type}:${f.id}`}>
                  {f.type === 'folder' ? '📁' : f.type === 'category' ? '🏷️' : '🔖'} {f.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Welcher Ordner/Kategorie soll die KI analysieren?
            </p>
          </div>
          
          {/* Confidence Threshold Slider */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Konfidenz-Schwelle: <span className="text-purple-600 dark:text-purple-400 font-bold">{confidenceThreshold}%</span>
            </label>
            <input
              type="range"
              min={50}
              max={99}
              value={confidenceThreshold}
              onChange={e => setConfidenceThreshold(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>50% (mehr Treffer)</span>
              <span>99% (präziser)</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Nur E-Mails mit einer Konfidenz über diesem Wert werden der Spalte zugeordnet.
            </p>
          </div>
          
          {/* Provider */}
          {providers.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">AI Provider</label>
              <select
                value={providerId}
                onChange={e => setProviderId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
              >
                {providers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.defaultModel}) {p.isDefault ? '★' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Automatic Actions Toggle */}
          <button
            type="button"
            onClick={() => setShowActions(!showActions)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showActions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <Play className="w-4 h-4" />
            Automatische Aktionen
            {actionsEnabled && <span className="text-green-500 text-xs ml-1">● aktiv</span>}
          </button>
          
          {showActions && (
            <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              {/* Enable Actions */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="actionsEnabled"
                  checked={actionsEnabled}
                  onChange={e => setActionsEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="actionsEnabled" className="text-sm font-medium">
                  Aktionen aktivieren
                </label>
              </div>
              
              {actionsEnabled && (
                <>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Wenn eine E-Mail dieser Spalte zugeordnet wird, können automatisch folgende Aktionen ausgeführt werden:
                  </p>
                  
                  {/* Move to Folder */}
                  <div className="space-y-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="actionMove"
                        checked={actionMoveEnabled}
                        onChange={e => setActionMoveEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <FolderInput className="w-4 h-4 text-gray-500" />
                      <label htmlFor="actionMove" className="text-sm font-medium">
                        In Ordner verschieben
                      </label>
                    </div>
                    {actionMoveEnabled && (
                      <select
                        value={actionMoveFolder}
                        onChange={e => setActionMoveFolder(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                      >
                        <option value="">Ordner wählen...</option>
                        {folders.filter(f => f.type === 'folder').map(f => (
                          <option key={f.id} value={f.id}>📁 {f.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {/* Set Category */}
                  <div className="space-y-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="actionCategory"
                        checked={actionCategoryEnabled}
                        onChange={e => setActionCategoryEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Tag className="w-4 h-4 text-gray-500" />
                      <label htmlFor="actionCategory" className="text-sm font-medium">
                        Kategorie setzen
                      </label>
                      <span className="text-xs text-gray-400">(nur Microsoft)</span>
                    </div>
                    {actionCategoryEnabled && (
                      <select
                        value={actionCategory}
                        onChange={e => setActionCategory(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                      >
                        <option value="">Kategorie wählen...</option>
                        {folders.filter(f => f.type === 'category').map(f => (
                          <option key={f.id} value={f.name}>🏷️ {f.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {/* Create Task */}
                  <div className="space-y-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="actionTask"
                        checked={actionTaskEnabled}
                        onChange={e => setActionTaskEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <ListTodo className="w-4 h-4 text-gray-500" />
                      <label htmlFor="actionTask" className="text-sm font-medium">
                        Aufgabe erstellen
                      </label>
                    </div>
                    {actionTaskEnabled && (
                      <div className="space-y-2 pl-6">
                        {/* Task List */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Aufgabenliste</label>
                          <select
                            value={actionTaskListId}
                            onChange={e => setActionTaskListId(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                          >
                            <option value="">Liste wählen...</option>
                            {taskLists.map(list => (
                              <option key={list.id} value={list.id}>
                                {list.name} {list.isDefault ? '★' : ''}
                              </option>
                            ))}
                          </select>
                          {!mailAccountId && (
                            <p className="text-xs text-yellow-600 mt-1">Kein Mail-Account ausgewählt</p>
                          )}
                        </div>
                        
                        {/* Title Mode */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Aufgabentitel</label>
                          <select
                            value={actionTaskTitleMode}
                            onChange={e => setActionTaskTitleMode(e.target.value as 'subject' | 'custom' | 'ai')}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                          >
                            <option value="subject">E-Mail Betreff verwenden</option>
                            <option value="ai">Titel durch KI erzeugen</option>
                            <option value="custom">Eigener Titel</option>
                          </select>
                        </div>
                        
                        {actionTaskTitleMode === 'custom' && (
                          <input
                            type="text"
                            value={actionTaskCustomTitle}
                            onChange={e => setActionTaskCustomTitle(e.target.value)}
                            placeholder="Eigener Aufgabentitel..."
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                          />
                        )}
                        
                        {/* Due Days */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Fälligkeit (Tage ab heute)</label>
                          <input
                            type="number"
                            value={actionTaskDueDays ?? ''}
                            onChange={e => setActionTaskDueDays(e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="Keine Fälligkeit"
                            min={0}
                            max={365}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                          />
                        </div>
                        
                        {/* Include Mail Link */}
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="actionTaskLink"
                            checked={actionTaskIncludeLink}
                            onChange={e => setActionTaskIncludeLink(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="actionTaskLink" className="text-xs text-gray-600 dark:text-gray-400">
                            Link zur E-Mail in Aufgabe einfügen
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Execute Once */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <input
                      type="checkbox"
                      id="executeOnce"
                      checked={actionExecuteOnce}
                      onChange={e => setActionExecuteOnce(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="executeOnce" className="text-xs text-gray-600 dark:text-gray-400">
                      Aktionen nur bei erster Erkennung ausführen (nicht bei Re-Evaluierung)
                    </label>
                  </div>
                  
                  {/* Min Confidence for Actions */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Min. Konfidenz für Aktionen (optional)
                    </label>
                    <input
                      type="number"
                      value={actionMinConfidence ?? ''}
                      onChange={e => setActionMinConfidence(e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder={`Standard: ${confidenceThreshold}%`}
                      min={50}
                      max={100}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Höherer Wert als Spalten-Konfidenz = sicherere Aktionen
                    </p>
                  </div>
                  
                  {/* Run Actions Once Button - only in edit mode */}
                  {isEditMode && editColumn && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={handleRunActionsOnce}
                        disabled={runningActions || !mailAccountId}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {runningActions ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                        Einmalig ausführen
                      </button>
                      <p className="text-xs text-gray-400 mt-1 text-center">
                        Führt Aktionen für alle aktuell zugeordneten E-Mails aus
                      </p>
                      {runActionsResult && (
                        <div className="mt-2 p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-xs">
                          <p className="font-medium">{runActionsResult.processed} E-Mails verarbeitet</p>
                          {runActionsResult.executed.length > 0 && (
                            <p className="text-green-600">{runActionsResult.executed.length} Aktionen ausgeführt</p>
                          )}
                          {runActionsResult.errors.length > 0 && (
                            <p className="text-red-600">{runActionsResult.errors.length} Fehler</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Advanced Settings Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Erweiterte Einstellungen
          </button>
          
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium mb-1">Icon</label>
                  <input
                    type="text"
                    value={icon}
                    onChange={e => setIcon(e.target.value)}
                    maxLength={2}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent text-center text-xl"
                  />
                </div>
                
                {/* Color */}
                <div>
                  <label className="block text-sm font-medium mb-1">Farbe</label>
                  <input
                    type="color"
                    value={color}
                    onChange={e => setColor(e.target.value)}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
              
              {/* Max Re-Evals */}
              <div>
                <label className="block text-sm font-medium mb-1">Max. Re-Evaluierungen</label>
                <input
                  type="number"
                  value={maxReEvals}
                  onChange={e => setMaxReEvals(parseInt(e.target.value) || 5)}
                  min={1}
                  max={20}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Wie oft eine E-Mail maximal neu bewertet werden kann.
                </p>
              </div>
              
              {/* Max Tokens Per Day */}
              <div>
                <label className="block text-sm font-medium mb-1">Max. Tokens / Tag</label>
                <input
                  type="number"
                  value={maxTokensPerDay}
                  onChange={e => setMaxTokensPerDay(parseInt(e.target.value) || 50000)}
                  min={1000}
                  step={1000}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Kostenlimit für diese Spalte (≈ {Math.round(maxTokensPerDay / 600)} Evaluierungen/Tag).
                </p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {/* Delete Section (Edit Mode Only) */}
          {isEditMode && onDelete && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              {!showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Spalte löschen
                </button>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <span className="text-sm text-red-600">
                    Wirklich löschen?
                  </span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ja, löschen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1 rounded text-sm text-gray-600 dark:text-gray-400"
                  >
                    Abbrechen
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading || !prompt.trim() || providers.length === 0}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {isEditMode ? 'Speichern' : 'Erstellen'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
